/**
 * BKEWidget — Boter-Kaas-en-Eieren als geheime widget. Score-zone + game-zone met
 * outline (zoals de Klok); het bord is altijd een passend vierkant met gelijke
 * cellen, ongeacht de widgethoogte. Tegen een minimax-computer (30% random).
 * Verbergen → TodayCard via SecretGameWidget.
 */
import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { T, Icon } from './widgetTokens';
import SecretGameWidget from './SecretGameWidget';
import { GameFrame, useFitSquare } from './gameKit';

const popIn = keyframes`0%{transform:scale(0);opacity:0}60%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}`;

const LINES = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]];
const getWinner = (b) => {
  for (const [a, c, d] of LINES) if (b[a] && b[a] === b[c] && b[a] === b[d]) return { player: b[a], line: [a, c, d] };
  return null;
};
const minimax = (b, max) => {
  const w = getWinner(b);
  if (w) return w.player === 'O' ? 10 : -10;
  if (b.every((c) => c)) return 0;
  let best = max ? -Infinity : Infinity;
  for (let i = 0; i < 9; i++) {
    if (!b[i]) {
      b[i] = max ? 'O' : 'X';
      const s = minimax(b, !max);
      b[i] = null;
      best = max ? Math.max(best, s) : Math.min(best, s);
    }
  }
  return best;
};
const bestMove = (b) => {
  const empty = b.map((c, i) => (c ? null : i)).filter((i) => i !== null);
  if (Math.random() < 0.3) {
    const safe = empty.filter((i) => {
      b[i] = 'O';
      const losing = empty.some((j) => {
        if (j === i || b[j]) return false;
        b[j] = 'X'; const wins = getWinner(b); b[j] = null;
        return wins?.player === 'X';
      });
      b[i] = null;
      return !losing;
    });
    const pool = safe.length ? safe : empty;
    return pool[Math.floor(Math.random() * pool.length)];
  }
  const moves = empty.map((i) => { b[i] = 'O'; const s = minimax(b, false); b[i] = null; return { i, s }; });
  const top = Math.max(...moves.map((m) => m.s));
  const bs = moves.filter((m) => m.s === top);
  return bs[Math.floor(Math.random() * bs.length)].i;
};

const XMark = () => (
  <svg viewBox="0 0 40 40" style={{ width: '56%', height: '56%' }}>
    <line x1="9" y1="9" x2="31" y2="31" stroke="#6366f1" strokeWidth="4.5" strokeLinecap="round" />
    <line x1="31" y1="9" x2="9" y2="31" stroke="#6366f1" strokeWidth="4.5" strokeLinecap="round" />
  </svg>
);
const OMark = () => (
  <svg viewBox="0 0 40 40" style={{ width: '56%', height: '56%' }}>
    <circle cx="20" cy="20" r="13" fill="none" stroke="#ef4444" strokeWidth="4.5" />
  </svg>
);

const StatNum = ({ v, l, c }) => (
  <Box sx={{ textAlign: 'center', minWidth: 38 }}>
    <Typography sx={{ fontFamily: T.font, fontSize: '1.15rem', fontWeight: 800, color: c, lineHeight: 1 }}>{v}</Typography>
    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', color: T.outline }}>{l}</Typography>
  </Box>
);

function BKEBoard() {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [over, setOver] = useState(false);
  const [winLine, setWinLine] = useState(null);
  const [status, setStatus] = useState('Jouw beurt');
  const [score, setScore] = useState({ player: 0, computer: 0, draw: 0 });
  const [last, setLast] = useState(-1);
  const [thinking, setThinking] = useState(false);
  const [fitRef, size] = useFitSquare();

  const reset = useCallback(() => {
    setBoard(Array(9).fill(null)); setOver(false); setWinLine(null);
    setStatus('Jouw beurt'); setLast(-1); setThinking(false);
  }, []);

  const play = (i) => {
    if (board[i] || over || thinking) return;
    const next = [...board]; next[i] = 'X';
    const pw = getWinner(next);
    if (pw) { setBoard(next); setWinLine(pw.line); setStatus('Gewonnen! 🎉'); setScore((s) => ({ ...s, player: s.player + 1 })); setOver(true); setLast(i); return; }
    if (next.every((c) => c)) { setBoard(next); setStatus('Gelijkspel'); setScore((s) => ({ ...s, draw: s.draw + 1 })); setOver(true); setLast(i); return; }
    setBoard(next); setStatus('Computer denkt…'); setLast(i); setThinking(true);
    setTimeout(() => {
      const cm = bestMove([...next]); next[cm] = 'O';
      const cw = getWinner(next);
      if (cw) { setBoard([...next]); setWinLine(cw.line); setStatus('Computer wint'); setScore((s) => ({ ...s, computer: s.computer + 1 })); setOver(true); setLast(cm); setThinking(false); return; }
      if (next.every((c) => c)) { setBoard([...next]); setStatus('Gelijkspel'); setScore((s) => ({ ...s, draw: s.draw + 1 })); setOver(true); setLast(cm); setThinking(false); return; }
      setBoard([...next]); setStatus('Jouw beurt'); setLast(cm); setThinking(false);
    }, 380 + Math.random() * 300);
  };

  const scoreZone = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 2.5 }}>
        <StatNum v={score.player} l="Jij" c="#6366f1" />
        <StatNum v={score.draw} l="Gelijk" c={T.outline} />
        <StatNum v={score.computer} l="Pc" c="#ef4444" />
      </Box>
      {over ? (
        <Box onClick={reset} sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
          px: 1.5, py: 0.4, borderRadius: '0.5rem', bgcolor: T.primary, color: '#fff',
          fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.74rem',
          '&:hover': { bgcolor: '#465469' },
        }}>
          <Icon name="replay" size={14} /> {status} · opnieuw
        </Box>
      ) : (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.secondary }}>
          {status}
        </Typography>
      )}
    </Box>
  );

  return (
    <GameFrame score={scoreZone}>
      <Box ref={fitRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{
          width: size, height: size,
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(3, 1fr)', gap: '5px',
        }}>
          {board.map((cell, i) => (
            <Box key={i} onClick={() => play(i)} sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '0.4rem',
              bgcolor: winLine?.includes(i) ? (cell === 'X' ? '#6366f120' : '#ef444420') : T.surfaceLow,
              cursor: cell || over ? 'default' : 'pointer', transition: 'background 0.15s',
              '&:hover': !cell && !over ? { bgcolor: T.surfaceHigh } : {},
            }}>
              {cell && <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: last === i ? `${popIn} 0.25s ease-out` : 'none' }}>{cell === 'X' ? <XMark /> : <OMark />}</Box>}
            </Box>
          ))}
        </Box>
      </Box>
    </GameFrame>
  );
}

export default function BKEWidget({ settings, onSettingsChange, widget }) {
  return (
    <SecretGameWidget settings={settings} onSettingsChange={onSettingsChange} widget={widget}>
      <BKEBoard />
    </SecretGameWidget>
  );
}
